import "./LastSearchList.scss";
import PropTypes from "prop-types";
import { FormattedDate, FormattedMessage, FormattedPlural } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconCrossSpacing from "app/pages/.shared/static/icons/IconCrossSpacing";
import { STOPOVER_DIRECTIONS } from "app/constants";
import { isSameMonth } from "date-fns";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { useContext } from "react";
import AppGlobalsContext from "app/AppGlobalsContext";

const LastSearchList = ({ items = [], onRemoveSearch, separator, onItemClick }) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;

	if (items?.length === 0) {
		return null;
	}

	const separatorElement = separator || <span className="last-search-list__separator"> · </span>;

	return (
		<div className="last-search-list">
			{items.map((item, index) => {
				const stopoverDirectionMessageId =
					item?.stopoverDirection === STOPOVER_DIRECTIONS.OUTBOUND
						? "sdp.search.stopover.outbound.label"
						: "sdp.search.stopover.inbound.label";

				const hideMonth = isSameMonth(item.departureDate, item.endDate);

				return (
					<div
						key={index}
						className="last-search-list__item"
						data-testid={`last-search-item`}
						onClick={() => {
							onItemClick(item?.id);
						}}
					>
						<Button
							variant={BUTTON_VARIANTS.BORDERLESS}
							size={BUTTON_SIZES.LARGE}
							className="last-search-list__remove"
							data-testid="last-search-remove-button"
							onClick={event => {
								event.stopPropagation();
								onRemoveSearch(item?.id);
							}}
							aria-label="Supprimer"
							icon={<IconCrossSpacing />}
						/>
						<div className="last-search-list__content">
							<div className="last-search-list__route">
								{item.departureCity && (
									<>
										<Typography
											component="span"
											variant={TYPOGRAPHY_VARIANTS.REGULAR}
											className="last-search-list__departure-city"
											isBold={isMobile}
										>
											{item.departureCity}
										</Typography>{" "}
										-{" "}
									</>
								)}

								{item.stopoverCityLabel && (
									<>
										<Typography
											component="span"
											variant={TYPOGRAPHY_VARIANTS.REGULAR}
											className="last-search-list__stopover-city"
											isBold={isMobile}
										>
											{item.stopoverCityLabel}
										</Typography>{" "}
										-{" "}
									</>
								)}

								<Typography
									component="span"
									variant={TYPOGRAPHY_VARIANTS.REGULAR}
									className="last-search-list__destination"
									isBold={isMobile}
								>
									{item.destination}
								</Typography>
							</div>
							{separatorElement}
							<div className="last-search-list__meta">
								<FormattedDate
									value={item.departureDate}
									timeZone="UTC"
									day="numeric"
									month={!hideMonth ? "short" : undefined}
								/>{" "}
								-{" "}
								<FormattedDate
									value={item.endDate}
									timeZone="UTC"
									day="numeric"
									month="short"
								/>{" "}
								{separatorElement}{" "}
								<FormattedMessage
									id="traveler.count"
									values={{ count: item.passengerCount }}
								/>
								,{" "}
								<FormattedPlural
									value={item.roomCount}
									one={
										<FormattedMessage
											values={{ count: item.roomCount }}
											id="general.room.count.label.singular"
										/>
									}
									other={
										<FormattedMessage
											values={{ count: item.roomCount }}
											id="general.room.count.label.plural"
										/>
									}
								/>{" "}
								{item.cabin && (
									<>
										{separatorElement} {item.cabin}{" "}
									</>
								)}
								{item.stopoverDirection && (
									<>
										{separatorElement}{" "}
										<FormattedMessage id={stopoverDirectionMessageId} />
									</>
								)}
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};

LastSearchList.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			departure: PropTypes.string.isRequired,
			stopover: PropTypes.string,
			destination: PropTypes.string.isRequired,
			dateRange: PropTypes.string.isRequired,
			travelers: PropTypes.string.isRequired,
			room: PropTypes.string.isRequired,
			classType: PropTypes.string.isRequired,
			flightType: PropTypes.string.isRequired,
		})
	),
	onRemoveSearch: PropTypes.func,
	onItemClick: PropTypes.func,
	separator: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export default LastSearchList;
